import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Language } from "../domain/shared/localized-text.types";

export type { Language } from "../domain/shared/localized-text.types";
type LanguagePreference = Language | "auto";

interface LanguageContextValue {
  language: Language;
  preference: LanguagePreference;
  setPreference: (preference: LanguagePreference) => void;
}

const STORAGE_KEY = "apinativos-language-preference";

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const detectBrowserLanguage = (): Language => {
  if (typeof navigator === "undefined") {
    return "es";
  }

  const browserLanguage = navigator.language.toLowerCase();
  return browserLanguage.startsWith("es") ? "es" : "en";
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<LanguagePreference>(() => {
    if (typeof window === "undefined") {
      return "auto";
    }

    const storedPreference = window.localStorage.getItem(STORAGE_KEY);
    if (storedPreference === "es" || storedPreference === "en" || storedPreference === "auto") {
      return storedPreference;
    }

    return "auto";
  });

  const language = preference === "auto" ? detectBrowserLanguage() : preference;

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, preference);
    }

    document.documentElement.lang = language;
  }, [language, preference]);

  const value = useMemo(
    () => ({
      language,
      preference,
      setPreference: setPreferenceState,
    }),
    [language, preference]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
